import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  Badge
} from 'reactstrap';
import Select from 'react-select';
import CloudRestroUsers from '../../../../services/DatabaseService/CloudRestroUsers';
import { toast } from 'react-toastify';

const BulkAddModal = ({ isOpen, toggle, deviceId, allOrganizations, currentOrganizations, onSuccess }) => {
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Get organizations that are not already assigned to this device
  const availableOrganizations = useMemo(() => {
    const currentOrgCodes = new Set(currentOrganizations.map(org => org.orgCode));
    return allOrganizations.filter(org => !currentOrgCodes.has(org.orgCode));
  }, [allOrganizations, currentOrganizations]);

  // Filter organizations based on search text
  const filteredOrgs = useMemo(() => {
    if (!filterText) return availableOrganizations;
    
    const searchLower = filterText.toLowerCase();
    return availableOrganizations.filter(org =>
      (org.name?.toLowerCase() || "").includes(searchLower) ||
      (org.orgCode?.toLowerCase() || "").includes(searchLower) ||
      (org.email?.toLowerCase() || "").includes(searchLower)
    );
  }, [availableOrganizations, filterText]);

  // Create options for react-select
  const organizationOptions = useMemo(() => {
    return filteredOrgs.map(org => ({
      value: org.orgCode,
      label: `${org.name} (${org.orgCode})`,
      org: org
    }));
  }, [filteredOrgs]);

  const handleSubmit = async () => {
    if (selectedOrganizations.length === 0) {
      toast.warning("Please select at least one organization");
      return;
    }

    setSubmitting(true);
    try {
      const payload = selectedOrganizations.map(org => ({
        orgCode: org.value,
        deviceId: deviceId
      }));
      console.log("🚀 ~ handleSubmit ~ selectedOrganizations:", selectedOrganizations)

      await CloudRestroUsers.bulkInsertOrg(payload);
      toast.success(`Successfully added ${selectedOrganizations.length} organization(s)`);
      
      // Reset and close
      setSelectedOrganizations([]);
      setFilterText("");
      onSuccess();
    } catch (error) {
      console.error("Error adding organizations:", error);
      toast.error("Failed to add organizations");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedOrganizations([]);
    setFilterText("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="bx bx-plus-circle me-2"></i>Add Organizations to Device
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Select Organizations</Label>
          <Select
            isMulti
            options={organizationOptions}
            value={selectedOrganizations}
            onChange={setSelectedOrganizations}
            placeholder="Search and select organizations..."
            className="react-select"
            classNamePrefix="react-select"
            onInputChange={(value) => setFilterText(value)}
            noOptionsMessage={() => "No organizations available"}
          />
          <small className="text-muted">
            Available: {availableOrganizations.length} | Selected: {selectedOrganizations.length}
          </small>
        </FormGroup>

        {selectedOrganizations.length > 0 && (
          <div className="mt-3">
            <Label>Selected Organizations:</Label>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Table size="sm" bordered hover>
                <thead className="table-light">
                  <tr>
                    <th width="40">#</th>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Email</th>
                    <th width="60">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrganizations.map((org, index) => (
                    <tr key={org.value}>
                      <td>{index + 1}</td>
                      <td>{org.org?.name || org.label}</td>
                      <td>
                        <Badge color="primary" pill>{org.value}</Badge>
                      </td>
                      <td>
                        <small>{org.org?.email || '-'}</small>
                      </td>
                      <td className="text-center">
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          onClick={() => {
                            setSelectedOrganizations(prev => 
                              prev.filter(o => o.value !== org.value)
                            );
                          }}
                        >
                          <i className="bx bx-x"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {availableOrganizations.length === 0 && (
          <div className="alert alert-info mt-3">
            <i className="bx bx-info-circle me-2"></i>
            All organizations are already assigned to this device.
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={selectedOrganizations.length === 0 || submitting}
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Adding...
            </>
          ) : (
            <>
              <i className="bx bx-check me-2"></i>
              Add {selectedOrganizations.length > 0 ? `(${selectedOrganizations.length})` : ''}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkAddModal;
